import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import api from '../../../api/axios';
import BuyNumberModal from './BuyNumberModal';

const COUNTRY_FLAGS = {
  IN: '\u{1F1EE}\u{1F1F3}',
  US: '\u{1F1FA}\u{1F1F8}',
  GB: '\u{1F1EC}\u{1F1E7}',
  AU: '\u{1F1E6}\u{1F1FA}',
  SG: '\u{1F1F8}\u{1F1EC}',
};

const formatE164 = (e164) => {
  if (!e164) return '';
  if (e164.startsWith('+91') && e164.length === 13) {
    return `+91 ${e164.slice(3, 8)} ${e164.slice(8)}`;
  }
  if (e164.startsWith('+1') && e164.length === 12) {
    return `+1 ${e164.slice(2, 5)} ${e164.slice(5, 8)} ${e164.slice(8)}`;
  }
  return e164;
};

const formatCurrency = (amount, currency) => {
  if (currency === 'INR') return `\u20B9${amount}/month`;
  if (currency === 'USD') return `$${amount}/month`;
  return `${amount} ${currency}/month`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

const StatusBadge = ({ status }) => {
  const s = status?.toUpperCase?.() || '';
  if (s === 'ACTIVE') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-100">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
        Active
      </span>
    );
  }
  if (s === 'SUSPENDED') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-100 cursor-default relative group">
        <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />
        Payment Failed
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 text-center shadow-lg">
          Monthly payment failed. Number will be released if not renewed within the grace period.
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </span>
      </span>
    );
  }
  if (s === 'PENDING_PAYMENT') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-100">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0 animate-pulse" />
        Pending Payment
      </span>
    );
  }
  if (s === 'PENDING_RELEASE') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-100">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
        Releasing
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200 cursor-default relative group">
      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />
      Inactive
      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 text-center shadow-lg">
        This number is currently disabled and cannot receive or make calls.
        <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
      </span>
    </span>
  );
};

const ThreeDotMenu = ({ onRelease, openUpward = false }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        aria-label="More actions"
      >
        <Icon name="MoreVertical" size={16} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className={`absolute right-0 z-20 w-40 bg-white border border-gray-200 rounded-lg shadow-lg py-1 ${openUpward ? 'bottom-8' : 'top-8'}`}>
            <button
              onClick={() => { setOpen(false); onRelease(); }}
              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              Release Number
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const EmptyState = ({ onBuyNumber }) => (
  <div className="flex flex-col items-center justify-center py-16 px-4">
    <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
      <Icon name="Phone" size={28} className="text-blue-500" />
    </div>
    <h3 className="text-base font-semibold text-gray-900 mb-2">No Phone Numbers Yet</h3>
    <p className="text-sm text-gray-500 text-center max-w-xs mb-6">
      Purchase your first virtual number to start making and receiving AI-powered calls.
    </p>
    <button
      onClick={onBuyNumber}
      className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#2F55FF] text-white text-sm font-medium rounded-lg hover:bg-[#2547e0] transition-colors shadow-sm"
    >
      <Icon name="Plus" size={16} />
      Buy Your First Number
    </button>
  </div>
);

const ReleaseConfirmModal = ({ isOpen, number, onClose, onConfirm, loading }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white border border-gray-200 rounded-2xl shadow-xl w-full max-w-sm">
        <div className="flex items-center gap-3 p-6 border-b border-gray-100">
          <div className="p-2 bg-red-50 rounded-lg">
            <Icon name="AlertTriangle" size={20} className="text-red-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Release Number</h3>
          <button onClick={onClose} className="ml-auto p-2 hover:bg-gray-100 rounded-lg">
            <Icon name="X" size={18} className="text-gray-400" />
          </button>
        </div>
        <div className="p-6">
          <p className="text-sm text-gray-600 mb-1">
            Are you sure you want to release this number?
          </p>
          <p className="text-sm font-semibold text-gray-900 mb-4">{number}</p>
          <p className="text-xs text-gray-500 mb-6">
            The number will enter a 24-hour cooldown period before being fully released. You can cancel the release during this time.
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Releasing...' : 'Release Number'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PhoneNumbersSection = ({ onBalanceChange }) => {
  const [numbers, setNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [releaseTarget, setReleaseTarget] = useState(null);
  const [releasing, setReleasing] = useState(false);
  const fetchNumbers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get('/api/v1/phone-numbers');
      const data = res.data?.data;
      // Backend returns array directly (List<MerchantPhoneNumberResponse>)
      setNumbers(Array.isArray(data) ? data : data?.items || []);
    } catch (err) {
      console.error('Failed to fetch phone numbers:', err);
      setError('Failed to load phone numbers.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNumbers();
  }, [fetchNumbers]);


  const handleRelease = async () => {
    if (!releaseTarget) return;
    setReleasing(true);
    try {
      const encoded = releaseTarget.replace('+', '');
      await api.delete(`/api/v1/phone-numbers/${encoded}`);
      setReleaseTarget(null);
      fetchNumbers();
    } catch (err) {
      console.error('Failed to release number:', err);
      setError(err?.response?.data?.message || 'Failed to release number.');
    } finally {
      setReleasing(false);
    }
  };

  const handleBuyClose = () => {
    setShowBuyModal(false);
    fetchNumbers(); // Always refresh — status may have changed via webhook
  };

  const handleBuySuccess = () => {
    setShowBuyModal(false);
    fetchNumbers();
    onBalanceChange?.();
  };

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB]">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Phone Numbers</h3>
          <p className="text-sm text-gray-500 mt-0.5">Manage your virtual phone numbers and billing</p>
        </div>
        <button
          onClick={() => setShowBuyModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#2F55FF] text-white text-sm font-medium rounded-lg hover:bg-[#2547e0] transition-colors shadow-sm flex-shrink-0"
        >
          <Icon name="Plus" size={16} />
          Buy New Number
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="mx-6 mt-4 flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          <Icon name="AlertCircle" size={16} className="flex-shrink-0" />
          {error}
          <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600">
            <Icon name="X" size={14} />
          </button>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
        </div>
      ) : numbers.length === 0 ? (
        <EmptyState onBuyNumber={() => setShowBuyModal(true)} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-[#E5E7EB]">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Phone Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Monthly Rent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Next Billing
                </th>
                <th className="px-6 py-3 w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E7EB]">
              {numbers.map((item, idx) => (
                <tr
                  key={item?.id || item?.e164}
                  className="hover:bg-[#F9FAFB] transition-colors"
                  style={{ height: '60px' }}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2.5">
                      <span className="text-lg leading-none">{COUNTRY_FLAGS[item?.country] || ''}</span>
                      <span className="text-sm font-medium text-gray-900">{formatE164(item?.e164)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge status={item?.status} />
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-700 font-medium">
                      {formatCurrency(item?.monthlyFee || item?.monthly_fee, item?.currency)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {(item?.nextBillingDate || item?.next_billing_date) ? (
                      <span className="text-sm text-gray-700">{formatDate(item?.nextBillingDate || item?.next_billing_date)}</span>
                    ) : (
                      <span className="text-sm text-gray-400">&mdash;</span>
                    )}
                  </td>
                  <td className="px-3 py-4">
                    {(item?.status?.toUpperCase() === 'ACTIVE' || item?.status?.toUpperCase() === 'SUSPENDED') && (
                      <ThreeDotMenu onRelease={() => setReleaseTarget(item?.e164)} openUpward={idx === numbers.length - 1} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {showBuyModal && (
        <BuyNumberModal
          onClose={handleBuyClose}
          onSuccess={handleBuySuccess}
        />
      )}

      <ReleaseConfirmModal
        isOpen={!!releaseTarget}
        number={releaseTarget ? formatE164(releaseTarget) : ''}
        onClose={() => setReleaseTarget(null)}
        onConfirm={handleRelease}
        loading={releasing}
      />
    </div>
  );
};

export default PhoneNumbersSection;
